// <pfs-mfa-challenge>: generischer Login-Zeit-2.-Faktor-Schritt (PFS-558).
//
// Sitzt zwischen Passwort-OK und abgeschlossenem Login. Der Host/BFF hat zuvor
// pfs-auth /api/login/begin server-seitig aufgerufen (Client-Basic-Auth bleibt
// server-seitig — NIE im Browser) und reicht challengeId + angebotene Faktoren
// in die WC. Die WC sammelt die 2.-Faktor-Antwort und POSTet sie an einen vom
// Host bereitgestellten Verify-Endpoint (Cookie-Auth), der sie server-seitig an
// /api/login/mfa-verify weiterreicht und den authorization_code zurückgibt.
//
// Bewusst entkoppelt: die WC kennt KEINE Client-Secrets und ruft pfs-auth NICHT
// direkt — sie spricht nur den Host-Endpoint und meldet das Ergebnis per Event.
//
// Attribute:
//   data-challenge-id   die offene Challenge-ID aus /login/begin (Pflicht).
//   data-factors        Komma-Liste angebotener Faktor-Typen, z.B. "totp,recovery".
//   data-verify-url     Host-Endpoint, der an /api/login/mfa-verify weiterreicht (Pflicht).
//   data-return-to      optionales return_to (SSO-Handoff) — wird an den Verify-POST
//                       mitgereicht, damit die Server-Antwort {redirect} korrekt ist.
//
// Events (bubbles, composed):
//   pfs:mfa-success   detail = Antwort des Verify-Endpoints (z.B. { code, ... }).
//   pfs:mfa-error     detail = { status, code, detail } bei endgültigem Fehlschlag.

import { defineComponent } from "../../composition/define-component.js";
import { withShadow } from "../../composition/with-shadow.js";
import { SHADOW_RESET_CSS } from "../../styles/shadow-reset.js";
import { BUTTON_CSS } from "../../styles/button.js";
import { CARD_CSS } from "../../styles/card.js";
import { apiFetch, ApiError } from "../../store/api-fetch.js";

interface ChallengeHost extends HTMLElement {
  _selected?: string;
  _error?: string;
  _busy?: boolean;
}

const FACTOR_LABELS: Record<string, string> = {
  totp: "Authenticator-App",
  recovery: "Recovery-Code",
};

const FACTOR_HINTS: Record<string, string> = {
  totp: "6-stelliger Code aus deiner Authenticator-App",
  recovery: "einer deiner Einmal-Recovery-Codes",
};

const STYLES = `
${SHADOW_RESET_CSS}
:host { display: block; color: var(--pfs-color-on-surface); font-size: 0.9rem; }
${CARD_CSS}
.card { padding: 1.25rem; display: flex; flex-direction: column; gap: 1rem; max-width: 360px; }
h2 { margin: 0; font-size: 1.05rem; font-weight: 600; }
.muted { color: var(--pfs-color-on-surface-muted); font-size: 0.85rem; }
.tabs { display: flex; gap: 0.5rem; flex-wrap: wrap; }
.tab {
  font: inherit; cursor: pointer; padding: 0.35rem 0.75rem; border-radius: 9999px;
  border: 1px solid var(--pfs-color-outline); background: var(--pfs-color-surface);
  color: var(--pfs-color-on-surface);
}
.tab[aria-pressed="true"] { background: var(--pfs-color-primary); color: var(--pfs-color-on-primary); border-color: transparent; }
/* Einheitlich mit <pfs-mfa-enroll> (PFS-559): klar erkennbares OTP-Feld —
   kontrastierender Hintergrund + kräftiger Border, mono/zentriert/gespreizt. */
.code-input {
  font-family: ui-monospace, monospace; font-size: 1.35rem; line-height: normal; text-align: center;
  letter-spacing: 0.3em; text-indent: 0.3em;
  padding: 0.8rem 0.5rem 0.6rem; border: 1px solid var(--pfs-color-outline-strong);
  border-radius: var(--pfs-radius-sm, 0.375rem); width: 100%; box-sizing: border-box;
  background: var(--pfs-color-surface-sunken); color: var(--pfs-color-on-surface);
}
.code-input::placeholder { color: var(--pfs-color-on-surface-subtle); }
/* Focus identisch zur <pfs-login-form> (@pfs/design Foundation). */
.code-input:focus { outline: none; border-color: var(--pfs-color-accent); box-shadow: var(--pfs-shadow-focus-ring, 0 0 0 3px rgba(59,130,246,0.35)); }
.error { color: var(--pfs-color-on-danger-container); font-size: 0.85rem; margin: 0; }
${BUTTON_CSS}
/* Busy-Spinner (Plattform-Kanon: async Aktion sichtbar machen). */
.spin { display:inline-block; width:0.9em; height:0.9em; margin-right:0.45em; vertical-align:-0.1em; border:2px solid currentColor; border-top-color:transparent; border-radius:50%; animation: pfs-spin 0.7s linear infinite; }
@keyframes pfs-spin { to { transform: rotate(360deg); } }
`;

function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

// Backup-/Recovery-Faktoren sind der Notnagel (nur wenn der primäre Faktor
// nicht verfügbar ist) — nie die primäre Wahl.
const BACKUP_FACTOR_TYPES = new Set(["recovery"]);

function factorsOf(host: HTMLElement): string[] {
  const raw = (host.getAttribute("data-factors") ?? "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  // Recovery/Backup IMMER ans Ende → nie Default (factors[0]) und stets der
  // letzte Tab, unabhängig von der Backend-Reihenfolge. Stabil: primäre
  // Faktoren behalten ihre relative Reihenfolge.
  return [...raw].sort(
    (a, b) => Number(BACKUP_FACTOR_TYPES.has(a)) - Number(BACKUP_FACTOR_TYPES.has(b)),
  );
}

defineComponent({
  tagName: "pfs-mfa-challenge",
  mixins: [withShadow()],
  render(host, ctx) {
    const root = ctx.shadowRoot;
    if (!root) return;
    const h = host as ChallengeHost;

    const factors = factorsOf(host);
    if (!h._selected || !factors.includes(h._selected)) {
      h._selected = factors[0] ?? "totp";
    }

    const challengeId = host.getAttribute("data-challenge-id") ?? "";
    const verifyUrl = host.getAttribute("data-verify-url") ?? "";
    // PFS-785: optionales return_to (SSO-Handoff) — wird an den Verify-POST
    // mitgereicht, damit die Server-Antwort {redirect} korrekt ist statt "/".
    const returnTo = host.getAttribute("data-return-to") ?? "";

    const submit = async (): Promise<void> => {
      if (h._busy) return;
      const input = root.querySelector<HTMLInputElement>("[data-response]");
      const response = (input?.value ?? "").trim();
      if (!response) {
        h._error = "Bitte den Code eingeben.";
        paint();
        return;
      }
      if (!challengeId || !verifyUrl) {
        h._error = "Konfigurationsfehler: Challenge oder Verify-Endpoint fehlt.";
        paint();
        return;
      }
      h._busy = true;
      h._error = undefined;
      paint();
      try {
        const verifyBody: Record<string, unknown> = {
          challenge_id: challengeId,
          factor_type: h._selected,
          response,
        };
        if (returnTo) verifyBody.return_to = returnTo; // PFS-785, symmetrisch zu <pfs-mfa-enroll>
        const res = await apiFetch<Record<string, unknown>>(verifyUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(verifyBody),
        });
        host.dispatchEvent(new CustomEvent("pfs:mfa-success", { bubbles: true, composed: true, detail: res }));
      } catch (e) {
        h._busy = false;
        const status = e instanceof ApiError ? e.status : 0;
        const code = e instanceof ApiError ? e.code : undefined;
        if (code === "too_many_attempts" || status === 429) {
          h._error = "Zu viele Fehlversuche. Bitte melde dich erneut an.";
        } else {
          h._error = "Code ungültig oder abgelaufen. Bitte erneut versuchen.";
        }
        host.dispatchEvent(
          new CustomEvent("pfs:mfa-error", { bubbles: true, composed: true, detail: { status, code, detail: h._error } }),
        );
        paint();
      }
    };

    function paint(): void {
      if (!root) return;
      const sel = h._selected ?? "totp";
      const tabs =
        factors.length > 1
          ? `<div class="tabs" role="group" aria-label="Faktor wählen">${factors
              .map(
                (f) =>
                  `<button type="button" class="tab" data-factor="${esc(f)}" aria-pressed="${f === sel}">${esc(
                    FACTOR_LABELS[f] ?? f,
                  )}</button>`,
              )
              .join("")}</div>`
          : "";
      const hint = FACTOR_HINTS[sel] ?? "deinen zweiten Faktor";
      root.innerHTML = `
        <style>${STYLES}</style>
        <div class="card">
          <h2>Bestätigung erforderlich</h2>
          <p class="muted">Gib ${esc(hint)} ein, um die Anmeldung abzuschließen.</p>
          ${tabs}
          <input class="code-input" type="text" inputmode="${sel === "totp" ? "numeric" : "text"}" autocomplete="one-time-code" placeholder="${sel === "totp" ? "123456" : "XXXX-XXXX"}" data-response ${h._busy ? "disabled" : ""} />
          ${h._error ? `<p class="error">${esc(h._error)}</p>` : ""}
          <button type="button" class="btn primary" data-submit ${h._busy ? "disabled" : ""}>${h._busy ? `<span class="spin" aria-hidden="true"></span>Prüfe…` : "Bestätigen"}</button>
        </div>`;

      root.querySelectorAll<HTMLElement>("[data-factor]").forEach((b) =>
        b.addEventListener("click", () => {
          h._selected = b.getAttribute("data-factor") ?? sel;
          h._error = undefined;
          paint();
        }),
      );
      root.querySelector("[data-submit]")?.addEventListener("click", () => void submit());
      const input = root.querySelector<HTMLInputElement>("[data-response]");
      input?.addEventListener("keydown", (e) => {
        if ((e as KeyboardEvent).key === "Enter") void submit();
      });
      input?.focus();
    }

    paint();
  },
});
